/**
 * manual-pairing.ts - Manual Pairing Modal
 *
 * Handles manual pairing for late joiners in Swiss tournaments.
 * Allows arbiter to:
 * - Pair two unpaired players
 * - Give a bye to an unpaired player
 * - Replace an existing bye with a real match
 */

import type { Player } from '../../../types/api';
import { getUnpairedPlayers, createManualPairing } from '../../api/tournament-api';
import { t } from '../../utils/i18n-helper';
import { escapeHtml } from '../../utils/security';

interface UnpairedPlayersData {
  round: number;
  unpairedPlayers: Player[];
  byeMatch: {
    id: string;
    player: Player;
    result?: string;
  } | null;
  totalPlayers: number;
  pairedCount: number;
}

let currentTournamentId: string = '';
let currentRound: number = 0;
let unpairedData: UnpairedPlayersData | null = null;

/**
 * Format player name for display
 */
function formatPlayerName(player: Player): string {
  const name = `${player.firstName} ${player.lastName}`.trim();
  const rating = player.rating ? ` (${player.rating})` : '';
  const title = player.title ? `${player.title} ` : '';
  return `${title}${name}${rating}`;
}

/**
 * Show the manual pairing modal
 * @param tournamentId - The tournament ID
 * @param round - The round number
 */
export async function showManualPairingModal(tournamentId: string, round: number): Promise<void> {
  currentTournamentId = tournamentId;
  currentRound = round;

  // Remove existing modal if present
  const existingModal = document.getElementById('manual-pairing-modal');
  if (existingModal) {
    existingModal.remove();
  }

  // Create modal container
  const modal = document.createElement('div');
  modal.id = 'manual-pairing-modal';
  modal.className = 'modal-backdrop';
  modal.innerHTML = `
    <div class="modal-content manual-pairing-modal">
      <div class="modal-header">
        <h3>${t('manualPairing.title', 'Manual Pairing')} - ${t('round.round', 'Round')} ${round}</h3>
        <button class="btn-close" onclick="closeManualPairingModal()">&times;</button>
      </div>
      <div class="modal-body" id="manual-pairing-content">
        <div class="loading">${t('common.loading', 'Loading...')}</div>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // Close on backdrop click
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeManualPairingModal();
    }
  });

  // Load unpaired players
  try {
    unpairedData = await getUnpairedPlayers(tournamentId, round);
    renderModalContent();
  } catch (error) {
    console.error('Error loading unpaired players:', error);
    const content = document.getElementById('manual-pairing-content');
    if (content) {
      content.innerHTML = `<p class="error">${t('manualPairing.errorLoading', 'Error loading player data')}</p>`;
    }
  }
}

/**
 * Render the modal content based on unpaired players data
 */
function renderModalContent(): void {
  const content = document.getElementById('manual-pairing-content');
  if (!content || !unpairedData) return;

  const { unpairedPlayers, byeMatch, totalPlayers, pairedCount } = unpairedData;

  // Use window.i18n directly for interpolation, or fallback
  const playersInfoText = window.i18n
    ? window.i18n.t('manualPairing.playersInfo', { paired: pairedCount, total: totalPlayers })
    : `Players: ${pairedCount}/${totalPlayers} paired`;

  let html = `
    <div class="manual-pairing-info">
      <p>${playersInfoText}</p>
    </div>
  `;

  // Show existing bye if present
  if (byeMatch) {
    html += `
      <div class="manual-pairing-section">
        <h4>${t('manualPairing.existingBye', 'Existing Bye')}</h4>
        <div class="bye-info">
          <span class="bye-player">${escapeHtml(formatPlayerName(byeMatch.player))}</span>
          <span class="bye-result">${byeMatch.result || '-'}</span>
        </div>
        ${unpairedPlayers.length > 0 ? `
          <p class="hint">${t('manualPairing.replaceByeHint', 'Select a player below to replace this bye with a real match')}</p>
        ` : ''}
      </div>
    `;
  }

  // Show unpaired players
  if (unpairedPlayers.length === 0) {
    html += `
      <div class="manual-pairing-section">
        <p>${t('manualPairing.allPlayersPaired', 'All players are already paired for this round.')}</p>
      </div>
    `;
  } else {
    html += `
      <div class="manual-pairing-section">
        <h4>${t('manualPairing.unpairedPlayers', 'Unpaired Players')} (${unpairedPlayers.length})</h4>
        <div class="unpaired-players-list">
          ${unpairedPlayers.map((player, index) => `
            <div class="unpaired-player-row">
              <input type="checkbox" id="player-${index}" name="selectedPlayer" value="${player.id}" class="player-checkbox">
              <label for="player-${index}">${escapeHtml(formatPlayerName(player))}</label>
            </div>
          `).join('')}
        </div>
      </div>
    `;

    // Action buttons
    html += `
      <div class="manual-pairing-actions">
        <div class="action-group">
          <button class="btn btn-primary" onclick="executeManualPairing('match')" id="pair-match-btn" disabled>
            ${t('manualPairing.pairPlayers', 'Pair Selected Players')}
          </button>
          <span class="action-hint">${t('manualPairing.selectTwoPlayers', 'Select 2 players')}</span>
        </div>
        ${byeMatch ? `
          <div class="action-group">
            <button class="btn btn-secondary" onclick="executeManualPairing('replaceBye')" id="replace-bye-btn" disabled>
              ${t('manualPairing.replaceBye', 'Replace Bye')}
            </button>
            <span class="action-hint">${t('manualPairing.selectOnePlayer', 'Select 1 player')}</span>
          </div>
        ` : ''}
        <div class="action-group">
          <button class="btn btn-secondary" onclick="executeManualPairing('bye')" id="give-bye-btn" disabled>
            ${t('manualPairing.giveBye', 'Give Bye')}
          </button>
          <span class="action-hint">${t('manualPairing.selectOnePlayer', 'Select 1 player')}</span>
        </div>
      </div>
      <div class="color-options" id="color-options" style="display: none;">
        <h4>${t('manualPairing.assignColors', 'Assign Colors')}</h4>
        <div class="color-assignment" id="color-assignment"></div>
      </div>
      <div class="bye-options" id="bye-options" style="display: none;">
        <label>
          <input type="radio" name="byePoints" value="1" checked>
          ${t('manualPairing.fullPointBye', 'Full-point bye (1.0)')}
        </label>
        <label>
          <input type="radio" name="byePoints" value="0.5">
          ${t('manualPairing.halfPointBye', 'Half-point bye (0.5)')}
        </label>
        <label>
          <input type="radio" name="byePoints" value="0">
          ${t('manualPairing.zeroPointBye', 'Zero-point bye (0.0)')}
        </label>
      </div>
    `;
  }

  content.innerHTML = html;

  // Add event listeners for checkboxes
  const checkboxes = content.querySelectorAll('.player-checkbox');
  checkboxes.forEach(cb => {
    cb.addEventListener('change', updateButtonStates);
  });
}

/**
 * Update button states based on selected players
 */
function updateButtonStates(): void {
  const checkboxes = document.querySelectorAll<HTMLInputElement>('.player-checkbox:checked');
  const selectedCount = checkboxes.length;
  const selectedIds = Array.from(checkboxes).map(cb => cb.value);

  const pairBtn = document.getElementById('pair-match-btn') as HTMLButtonElement;
  const replaceByeBtn = document.getElementById('replace-bye-btn') as HTMLButtonElement;
  const giveByeBtn = document.getElementById('give-bye-btn') as HTMLButtonElement;
  const byeOptions = document.getElementById('bye-options');
  const colorOptions = document.getElementById('color-options');
  const colorAssignment = document.getElementById('color-assignment');

  if (pairBtn) {
    pairBtn.disabled = selectedCount !== 2;
  }
  if (replaceByeBtn) {
    replaceByeBtn.disabled = selectedCount !== 1;
  }
  if (giveByeBtn) {
    giveByeBtn.disabled = selectedCount !== 1;
    // Show/hide bye options
    if (byeOptions) {
      byeOptions.style.display = selectedCount === 1 ? 'block' : 'none';
    }
  }

  // Show/hide and update color options when 2 players are selected
  if (colorOptions && colorAssignment) {
    if (selectedCount === 2 && unpairedData) {
      const player1 = unpairedData.unpairedPlayers.find(p => p.id === selectedIds[0]);
      const player2 = unpairedData.unpairedPlayers.find(p => p.id === selectedIds[1]);
      if (player1 && player2) {
        colorOptions.style.display = 'block';
        colorAssignment.innerHTML = `
          <label class="color-radio">
            <input type="radio" name="whitePlayer" value="${player1.id}" checked>
            <span class="color-indicator white">⬜</span>
            ${escapeHtml(formatPlayerName(player1))}
          </label>
          <label class="color-radio">
            <input type="radio" name="whitePlayer" value="${player2.id}">
            <span class="color-indicator white">⬜</span>
            ${escapeHtml(formatPlayerName(player2))}
          </label>
        `;
      }
    } else {
      colorOptions.style.display = 'none';
      colorAssignment.innerHTML = '';
    }
  }
}

/**
 * Execute the manual pairing action
 * @param action - The action type: 'match', 'bye', or 'replaceBye'
 */
export async function executeManualPairing(action: 'match' | 'bye' | 'replaceBye'): Promise<void> {
  const checkboxes = document.querySelectorAll<HTMLInputElement>('.player-checkbox:checked');
  const selectedIds = Array.from(checkboxes).map(cb => cb.value);

  if (!currentTournamentId || !currentRound) return;

  try {
    let result;

    if (action === 'match') {
      if (selectedIds.length !== 2) {
        alert(t('manualPairing.selectTwoPlayers', 'Please select exactly 2 players'));
        return;
      }
      // Get the selected white player
      const whitePlayerRadio = document.querySelector<HTMLInputElement>('input[name="whitePlayer"]:checked');
      const whitePlayerId = whitePlayerRadio?.value || selectedIds[0];
      const blackPlayerId = selectedIds.find(id => id !== whitePlayerId) || selectedIds[1];

      result = await createManualPairing(currentTournamentId, {
        round: currentRound,
        player1Id: whitePlayerId,
        player2Id: blackPlayerId
      });
    } else if (action === 'bye') {
      if (selectedIds.length !== 1) {
        alert(t('manualPairing.selectOnePlayer', 'Please select exactly 1 player'));
        return;
      }
      const byePointsRadio = document.querySelector<HTMLInputElement>('input[name="byePoints"]:checked');
      const byePoints = byePointsRadio ? parseFloat(byePointsRadio.value) : 1.0;
      result = await createManualPairing(currentTournamentId, {
        round: currentRound,
        player1Id: selectedIds[0],
        isBye: true,
        byePoints
      });
    } else if (action === 'replaceBye') {
      if (selectedIds.length !== 1 || !unpairedData?.byeMatch) {
        alert(t('manualPairing.selectOnePlayer', 'Please select exactly 1 player'));
        return;
      }
      result = await createManualPairing(currentTournamentId, {
        round: currentRound,
        player1Id: selectedIds[0],
        replaceByeMatchId: unpairedData.byeMatch.id
      });
    }

    // Save IDs before closing modal (closeManualPairingModal clears them)
    const tournamentId = currentTournamentId;
    const round = currentRound;

    // Success - close modal and reload tournament
    closeManualPairingModal();

    // Save active tab before reload so we stay on pairings
    sessionStorage.setItem(`active-tab-${tournamentId}`, 'pairings');

    // Reload tournament to show updated pairings
    if (window.loadSingleTournament && tournamentId) {
      await window.loadSingleTournament(tournamentId);
    }

    // Show the current round
    if (window.showMatchRound && tournamentId) {
      setTimeout(() => {
        window.showMatchRound(tournamentId, round);
      }, 50);
    }
  } catch (error) {
    console.error('Error creating manual pairing:', error);
    alert(error instanceof Error ? error.message : t('manualPairing.errorCreating', 'Error creating pairing'));
  }
}

/**
 * Close the manual pairing modal
 */
export function closeManualPairingModal(): void {
  const modal = document.getElementById('manual-pairing-modal');
  if (modal) {
    modal.remove();
  }
  currentTournamentId = '';
  currentRound = 0;
  unpairedData = null;
}

// Export to window for onclick handlers
window.showManualPairingModal = showManualPairingModal;
window.closeManualPairingModal = closeManualPairingModal;
window.executeManualPairing = executeManualPairing;
